import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStateHook,
	createStore,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
} from 'react-sweet-state';

import {
	type AggAbTest,
	type EntityATI,
	type HydratedJiraIssue,
	isSearchCompassPartial,
	isSearchResultJiraIssue,
	type ProductCount,
	type QueryInfo,
	type ResultsSort,
	type ResultsSortField,
	type ResultsSortOrder,
	type SearchPageEdge,
	type SearchPageResults,
	SORT_FIELD_KEY,
	SORT_ORDER,
	type TeamDetails,
} from '@atlassian/dynamic-cards-search-client';

import { QueryState } from '../../../common/constants/analytics';
import {
	type FilterId,
	getFilterIdOrdering,
	type TypeFilterValueKey,
	universalTypeFilterId,
} from '../../../common/constants/filters';
import { type NounKeys } from '../../../common/constants/nouns';
import { type PrimaryProductKey, ProductKeys } from '../../../common/constants/products';
import {
	type LastModifiedValue,
	type SearchQueryStringParams,
} from '../../../common/constants/schemas/query-params';
import { type OutboundAuthConfigsType, type QueryParams } from '../../../common/types';
import { parseQueryParams } from '../../../common/utils/query-params';
import { getTeamId } from '../../../common/utils/teams';
import { type SelectFilterOption, type SelectFilterOptionChange } from '../../filters';
import { isSelectFilter } from '../../filters/base-select-filter';
import { type CloudConfig } from '../../filters/types';
import { type ResultsFetchError } from '../../results/fetch-results';
import { type AllFilterStates, type AllFilterValues, type Filters } from './filters';
import { getEntitiesForQuery as getUniversalEntitiesForQuery } from './filters/universal-type/utils';
import { FilterUtils } from './filters/utils';

export type ProductResultCount = {
	[key: string]: number;
};

export type SearchStoreContainerType<TFilter> = {
	allFilterExcludedProducts: ProductKeys[];
	allFilters: Record<string, TFilter>;
	availableProducts: ProductKeys[];
	config: CloudConfig;
	queryParams: QueryParams;
	setQueryParams: (_: Partial<SearchQueryStringParams>) => void;
};

type PaginationToken = {
	cursor: string;
};

export type SearchStoreContainerProps = SearchStoreContainerType<AllFilterValues>;

export type SearchStoreState = {
	abTest?: SearchPageResults['abTest'];
	// Products excluded from query by all products (.e.g. Can only search via specific product filter)
	allFilterExcludedProducts: ProductKeys[];
	// Auto correction
	autoCorrection?: string;
	// Product states
	availableProducts: ProductKeys[];
	// Filter States
	filters: Record<string, AllFilterStates | undefined>;
	filterSelectionOrder: string[];
	// Search Input states
	inputQuery: string;
	isCompassTeamsLoading?: boolean;

	isJiraHydrationLoading?: boolean;

	isLoading: boolean;
	isLoadingMore: boolean;
	originalQuery?: string;
	// Pagination
	// This is not multi-product aware, but is a single cursor for all products
	// However, this structure is one step towards multi product pagination
	paginationTokens: PaginationToken[];

	primaryProduct: PrimaryProductKey | null;
	productResultCountMap: ProductResultCount | null;
	queryUpdatedTime?: number;

	requestId?: string;
	// Errors within the GraphQL response object such as downstream errors
	// OR if the GraphQL request fails.
	resultError?: ResultsFetchError;

	results?: SearchPageResults;

	resultsSort: ResultsSort;
	retryCount: number;
	searchQuery: string;

	selectedProducts: ProductKeys[];

	shownCount: number;
	version: number;
};

const initialState = {
	// Search Input states
	inputQuery: '',
	searchQuery: '',

	// Product States
	primaryProduct: null,

	// Filter States
	filters: {},
	filterSelectionOrder: [],
	selectedProducts: [],
	availableProducts: [],
	allFilterExcludedProducts: [],

	// Rest of store states
	resultsSort: {
		field: SORT_FIELD_KEY.LAST_MODIFIED_DATE,
		order: SORT_ORDER.DESC,
	},
	retryCount: 0,
	version: 1,
	queryUpdatedTime: Date.now(),
	isLoading: false,
	isJiraHydrationLoading: false,
	isCompassTeamsLoading: false,
	shownCount: 0,
	results: undefined,
	resultError: undefined,
	productResultCountMap: null,
	paginationTokens: [],
	isLoadingMore: false,
} satisfies SearchStoreState;

const convertQueryParamsToStringArray = (
	queryParams: QueryParams,
	keys: string[],
): Record<string, string[]> => {
	return keys.reduce((acc, key) => {
		if (queryParams[key] === undefined) {
			return acc;
		}
		if (Array.isArray(queryParams[key])) {
			return {
				...acc,
				[key]: queryParams[key],
			} as Record<string, string[]>;
		}
		if (typeof queryParams[key] === 'string' || typeof queryParams[key] === 'boolean') {
			return { ...acc, [key]: [(queryParams[key] as string | boolean).toString() as string] };
		}
		return acc;
	}, {});
};

const getUpdatedFilterSelectionOrder = (
	previousOrder: string[],
	newFilterId: string,
	selected?: boolean,
) => {
	let filterSelectionOrder = new Set([...previousOrder, newFilterId]);
	if (!selected) {
		filterSelectionOrder.delete(newFilterId);
	}

	return [...filterSelectionOrder];
};

/**
 * Only these keys can get set before the updateQueryParams loop when updateQueryParams=true in setSearchStoreState
 */
const safeStateKeys = ['filterSelectionOrder', 'retryCount', 'paginationTokens'];

type SetSearchStoreStateOptions = {
	updateQueryParams?: boolean;
};
/**
 * Please use setSearchStoreState instead of setState within the Search Store controller.
 *
 * This is because we treat the URL as the source of truth for some state. Filter value states are set in the URL directly and then we listen to URL state changs to represent them in our state.
 *
 * @param newState - state properties to set
 * @param {boolean} options.updateQueryParams - [false] Whether a setState call should trigger an URL update
 *
 * Notes on updateQueryParams:
 *
 * When false, this function acts like a standard setState
 * When true, this function will allow only the {safeStateKeys} to be set and will then use the provided query/filter etc state to set the URL directly, relying on the URL observer to complete the loop
 *
 * Non-url updates should NEVER trigger the setQueryParams callback. Otherwise, this will cause infinite loops or extra render cycles.
 **/
const setSearchStoreState =
	(
		newState: Partial<SearchStoreState>,
		options?: SetSearchStoreStateOptions,
	): Action<SearchStoreState, SearchStoreContainerProps> =>
	({ getState, setState }, { setQueryParams, allFilters, config }) => {
		// TODO future refactoring: we should instead query the newState object for any URL-state changes, then clean up the setState we perform, then update the URL. Then a consumer of this function will not need to care about any of this and can simply pass `newState` as expected
		if (!options?.updateQueryParams) {
			setState(newState);
			return;
		}

		// We still want to set variables in state that do NOT participate in the URL
		// Initially we'll allow-list this set, as the set of filters (the disallow set) will grow over time
		const safeNewState = Object.fromEntries(
			Object.entries(newState).filter(([key]) => safeStateKeys.includes(key)),
		);
		if (Object.keys(safeNewState).length > 0) {
			setState(safeNewState);
		}

		const { searchQuery: newSearchQuery, inputQuery: newInputQuery } = newState;
		const { inputQuery: oldInputQuery } = getState();

		const {
			selectedProducts,
			filters: allFilterStates,
			resultsSort: newResultsSort,
		} = {
			...getState(),
			...newState,
		} satisfies SearchStoreState;

		const staticFilters = Object.values(allFilters).filter((f) => {
			const filterState = f.getInitialState(config).getState();
			return selectedProducts.length === 0
				? filterState.universal
				: filterState.products.some((product) => selectedProducts.includes(product));
		});

		const filterQueryParamValues = staticFilters.map((staticFilter) => {
			const filterState = allFilterStates[staticFilter.id];

			if (!staticFilter.isCompatible(filterState)) {
				return {};
			}

			return staticFilter.withState(filterState, config).queryParams.get();
		});

		const filterQueryParams = Object.assign({}, ...filterQueryParamValues);
		const text = [newSearchQuery, newInputQuery, oldInputQuery].find(Boolean);

		const parsedQueryParams = parseQueryParams(
			{
				product: selectedProducts?.[0],
				text: text !== '' ? text : undefined,
				...filterQueryParams,
				...(newResultsSort && {
					sortField: newResultsSort.field,
					sortOrder: newResultsSort.order,
				}),
			},
			{ commaSeparatedValues: true },
		);
		setQueryParams(parsedQueryParams);
	};
export const unsafe__testingOnly_setSearchStoreState: (
	newState: Partial<SearchStoreState>,
	options?: SetSearchStoreStateOptions,
) => Action<SearchStoreState, SearchStoreContainerProps> = setSearchStoreState;

const privateStoreActions = {
	initialiseStore:
		(): Action<SearchStoreState, SearchStoreContainerProps> =>
		async (
			{ dispatch },
			{
				allFilters,
				queryParams,
				config,
				setQueryParams,
				availableProducts,
				allFilterExcludedProducts,
			},
		) => {
			const { getSelectedProducts, filtersByProduct, isMissingProductSelection } = FilterUtils(
				allFilters,
				{},
			);
			const selectedProducts = getSelectedProducts(queryParams);

			if (isMissingProductSelection(queryParams)) {
				setQueryParams({ ...queryParams, product: selectedProducts[0] });
				return;
			}

			const { staticFilterValues: staticFiltersByProduct } = filtersByProduct(selectedProducts);

			const resolvedQueryParams = await Promise.allSettled(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				staticFiltersByProduct.map((staticFilter: any) => {
					const queryParamsKeys = [staticFilter.queryParams.key];
					if (staticFilter.isSupportCustomValue && staticFilter.customValueFields) {
						queryParamsKeys.push(...staticFilter.customValueFields);
					}
					return staticFilter
						.getInitialState(config)
						.queryParams.set(convertQueryParamsToStringArray(queryParams, queryParamsKeys));
				}),
			);

			const filterStatesByProduct = resolvedQueryParams.flatMap((promise) => {
				switch (promise.status) {
					case 'fulfilled':
						return [promise.value.getState()];
					case 'rejected':
						return [];
				}
			});

			const { filterStates } = FilterUtils(staticFiltersByProduct, filterStatesByProduct);

			const sortField = queryParams.sortField || initialState.resultsSort.field;
			const sortOrder = queryParams.sortOrder || initialState.resultsSort.order;

			dispatch(
				setSearchStoreState(
					{
						filters: filterStates,
						resultsSort: {
							field: sortField as ResultsSortField,
							order: sortOrder as ResultsSortOrder,
						},
						selectedProducts,
						inputQuery: String(queryParams.text ?? ''),
						searchQuery: String(queryParams.text ?? ''),
						originalQuery: String(queryParams.originalQuery ?? ''),
						availableProducts,
						allFilterExcludedProducts,
					},
					{
						updateQueryParams: false, // onInit should not update the query params
					},
				),
			);
		},
	// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
	updateStore:
		(): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ dispatch, getState }, { allFilters, queryParams, config, availableProducts }) => {
			const { filters: filterStates, inputQuery, searchQuery, originalQuery } = getState();

			const { getSelectedProducts, filtersByProduct } = FilterUtils(allFilters, filterStates);
			const selectedProducts = getSelectedProducts(queryParams);

			const { staticFilterValues: staticFiltersByProduct, filterStates: filterStatesByProduct } =
				filtersByProduct(selectedProducts);

			const resolvedQueryParams = await Promise.allSettled(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				staticFiltersByProduct.map((staticFilter: any) => {
					const filterState = filterStatesByProduct[staticFilter.id];
					const queryParamsKeys = [staticFilter.queryParams.key];
					if (staticFilter.isSupportCustomValue && staticFilter.customValueFields) {
						queryParamsKeys.push(...staticFilter.customValueFields);
					}
					const queryParamStringArray = convertQueryParamsToStringArray(
						queryParams,
						queryParamsKeys,
					);

					if (!staticFilter.isCompatible(filterState)) {
						return staticFilter.getInitialState(config).queryParams.set(queryParamStringArray);
					}

					return staticFilter.withState(filterState, config).queryParams.set(queryParamStringArray);
				}),
			);

			const fullfilledFilterStates = resolvedQueryParams.flatMap((promise) => {
				switch (promise.status) {
					case 'fulfilled':
						return [promise.value.getState()];
					case 'rejected':
						return [];
				}
			});

			const { getFiltersWithSelection, filterStates: resolvedFilterStates } = FilterUtils(
				staticFiltersByProduct,
				fullfilledFilterStates,
			);

			// In order, ensure all filters with values are added to selection
			// Ensures filters enter selection order from resume-from-url
			// TODO: this should be done with the global ordering, but we don't have that in constants yet
			let { filterSelectionOrder } = getState();
			getFiltersWithSelection().map((filterId: FilterId) => {
				filterSelectionOrder = getUpdatedFilterSelectionOrder(
					filterSelectionOrder,
					filterId,
					resolvedFilterStates[filterId]?.hasSelection,
				);
			});

			const sortField = queryParams.sortField || initialState.resultsSort.field;
			const sortOrder = queryParams.sortOrder || initialState.resultsSort.order;

			dispatch(
				setSearchStoreState(
					{
						filters: resolvedFilterStates,
						resultsSort: {
							field: sortField as ResultsSortField,
							order: sortOrder as ResultsSortOrder,
						},
						selectedProducts: getSelectedProducts(queryParams),
						filterSelectionOrder: filterSelectionOrder,
						inputQuery: String(queryParams.text ?? inputQuery),
						searchQuery: String(queryParams.text ?? searchQuery),
						originalQuery: String(queryParams.originalQuery ?? originalQuery),
						availableProducts,
						paginationTokens: [],
					},
					{ updateQueryParams: false }, // onUpdate lifecycle should never update the query params
				),
			);
		},
	setFilter:
		(
			state: AllFilterStates,
			shouldSetQueryParams?: boolean,
		): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ dispatch, getState }, { allFilters, config }) => {
			const staticFilter = allFilters[state.id];
			const { filters: allFiltersState } = getState();

			if (!staticFilter.isCompatible(state)) {
				return;
			}

			const filterWithState = staticFilter.withState(state, config).getState();
			dispatch(
				setSearchStoreState(
					{
						filters: {
							...allFiltersState,
							[staticFilter.id]: filterWithState,
						},
					},
					{
						updateQueryParams: shouldSetQueryParams,
					},
				),
			);
		},
	updateFilterSelectionOrder:
		(filterId: string, selected: boolean): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ dispatch, getState }) => {
			const { filterSelectionOrder: oldSelectionOrder } = getState();

			const newFilterSelectionOrder = getUpdatedFilterSelectionOrder(
				oldSelectionOrder,
				filterId,
				selected,
			);

			dispatch(
				setSearchStoreState({
					filterSelectionOrder: newFilterSelectionOrder,
				}),
			);
		},
} as const;

export const actions = {
	loadInitialSelectFilterOptions:
		(props: { filterId: string }): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (
				!staticSelectFilter.isCompatible(filterState) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			const newFilter = staticSelectFilter.withState(filterState, config);

			// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
			await newFilter.loadInitialOptions((stateChange) => {
				dispatch(privateStoreActions.setFilter(stateChange));
			});
		},
	clearSelectFilterOptions:
		(props: { filterId: string }): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (
				!staticSelectFilter.isCompatible(filterState) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			const newFilterState = staticSelectFilter.withState(filterState, config).clear().getState();

			dispatch(privateStoreActions.setFilter(newFilterState, true));
			dispatch(
				privateStoreActions.updateFilterSelectionOrder(props.filterId, newFilterState.hasSelection),
			);
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);
		},
	setSelectFilterOption:
		(props: {
			customValue?: Record<string, string>;
			filterId: string;
			optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
		}): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (
				!staticSelectFilter.isCompatible(filterState) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			const newFilterState = staticSelectFilter
				.withState(filterState, config)
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				.setOption(props.optionChange, props.customValue)
				.getState();

			dispatch(privateStoreActions.setFilter(newFilterState, true));
			dispatch(
				privateStoreActions.updateFilterSelectionOrder(props.filterId, newFilterState.hasSelection),
			);
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);
		},
	setSelectFilterLookupInput:
		(props: {
			filterId: string;
			query: string;
		}): Action<SearchStoreState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (
				!staticSelectFilter.isCompatible(filterState) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			await staticSelectFilter
				.withState(filterState, config)
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				.setLookupInput(props.query, (stateChange) => {
					dispatch(privateStoreActions.setFilter(stateChange));
				});
		},
	setBooleanFilterValue:
		(
			props: {
				filterId: string;
				value: boolean;
			},
			privateActions: {
				readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				readonly setFilter: (
					state: AllFilterStates,
					shouldSetQueryParams?: boolean,
				) => Action<SearchStoreState, SearchStoreContainerProps>;
				readonly updateFilterSelectionOrder: (
					filterId: string,
					selected: boolean,
				) => Action<SearchStoreState, SearchStoreContainerProps>; // TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
				readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
			} = privateStoreActions,
		): Action<SearchStoreState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { allFilters, config }) => {
			const staticBooleanFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (!staticBooleanFilter.isCompatible(filterState)) {
				return;
			}

			if (staticBooleanFilter.type !== 'boolean') {
				return;
			}

			const newBooleanFilterState = staticBooleanFilter
				.withState(filterState, config)
				.setValue(props.value)
				.getState();

			dispatch(privateActions.setFilter(newBooleanFilterState, true));
			dispatch(
				privateActions.updateFilterSelectionOrder(
					props.filterId,
					newBooleanFilterState.hasSelection,
				),
			);
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);
		},
	clearAllFilters:
		(): Action<SearchStoreState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { allFilters, config }) => {
			const { filters: allFilterStates } = getState();
			const staticFilters = Object.values(allFilters);

			dispatch(
				setSearchStoreState(
					{
						filters: clearFilters(allFilterStates, staticFilters, config),
						selectedProducts: [],
						filterSelectionOrder: [],
					},
					{ updateQueryParams: true },
				),
			);
		},
	clearAllAvailableFilters:
		(): Action<SearchStoreState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { allFilters, config }) => {
			const staticFilters = Object.values(allFilters);
			const { filters: allFilterStates } = getState();

			dispatch(
				setSearchStoreState(
					{
						filters: clearFilters(allFilterStates, staticFilters, config),
						filterSelectionOrder: [],
						resultsSort: initialState.resultsSort,
					},
					{ updateQueryParams: true },
				),
			);
		},
	// Product filter actions
	selectProductFilter:
		(productKey?: ProductKeys): Action<SearchStoreState, SearchStoreContainerProps> =>
		({ getState, dispatch }) => {
			const { selectedProducts, filters, filterSelectionOrder } = getState();
			if (productKey && selectedProducts.includes(productKey)) {
				return;
			}

			const persistedFilters: Record<string, AllFilterStates> = {};

			// Persist selected universal filters if they are relevant to the selected product
			for (const [id, filter] of Object.entries(filters)) {
				if (!filter) {
					continue;
				}

				if (
					filter.universal &&
					filter.hasSelection &&
					(!productKey || (productKey && filter.products.includes(productKey)))
				) {
					persistedFilters[id] = filter;
				}
			}

			// Type is a universal filter, but type needs to be cleared if we're filtering away from Atlas
			// This is because selecting a global noun sets product AND type values
			if (selectedProducts.includes(ProductKeys.Atlas)) {
				delete persistedFilters[universalTypeFilterId];
			}

			// Universal filters being persisted also retain their ordering
			const newFilterSelectionOrder = filterSelectionOrder.filter((f) => {
				return Object.keys(persistedFilters).includes(f);
			});

			dispatch(
				setSearchStoreState(
					{
						selectedProducts: productKey ? [productKey] : [],
						filterSelectionOrder: newFilterSelectionOrder,
						filters: persistedFilters,
						resultsSort: initialState.resultsSort,
					},
					{ updateQueryParams: true },
				),
			);
		},
	// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
	// together as they do the same thing
	unselectProductFilter:
		(productKey: ProductKeys): Action<SearchStoreState, SearchStoreContainerProps> =>
		({ getState, dispatch }) => {
			const { selectedProducts } = getState();

			if (selectedProducts.includes(productKey)) {
				dispatch(actions.selectProductFilter(undefined));
			}
		},
	// Search Input Actions
	setInputQuery:
		(inputQuery: string): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ inputQuery: inputQuery.trim() }));
		},
	setSearchQuery:
		(searchQuery: string): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({ searchQuery: searchQuery.trim() }, { updateQueryParams: true }),
			);
		},
	triggerSearch:
		(): Action<SearchStoreState> =>
		({ getState, dispatch }) => {
			const { inputQuery, searchQuery, retryCount, productResultCountMap } = getState();
			dispatch(
				setSearchStoreState({
					productResultCountMap: searchQuery === inputQuery ? productResultCountMap : null,
				}),
			);
			dispatch(
				setSearchStoreState(
					{
						searchQuery: inputQuery,
						retryCount: searchQuery === inputQuery ? retryCount + 1 : 0,
					},
					{ updateQueryParams: true },
				),
			);
		},
	retrySearch:
		(): Action<SearchStoreState> =>
		({ dispatch, getState }) => {
			dispatch(setSearchStoreState({ retryCount: getState().retryCount + 1 }));
		},
	setResultsSort:
		({ field, order }: ResultsSort): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState(
					{
						resultsSort: {
							field,
							order,
						},
						paginationTokens: [],
					},
					{
						updateQueryParams: true,
					},
				),
			);
		},
	resetSort:
		(): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					resultsSort: initialState.resultsSort,
					paginationTokens: [],
				}),
			);
		},
	// Rest of Search Actions
	setQueryUpdatedTime:
		(): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ queryUpdatedTime: Date.now() }));
		},
	incrementVersion:
		(): Action<SearchStoreState> =>
		({ dispatch, getState }) => {
			const { version } = getState();
			dispatch(setSearchStoreState({ version: version + 1 }));
		},
	setIsLoading:
		(isLoading: boolean): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ isLoading }));
		},
	// Results actions
	setResults:
		(
			results?: SearchStoreState['results'],
			error?: SearchStoreState['resultError'],
			requestId?: SearchStoreState['requestId'],
		): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isLoading: false,
					results,
					resultError: error,
					requestId,
				}),
			);
		},
	appendMoreResults:
		(results: SearchStoreState['results']): Action<SearchStoreState> =>
		({ dispatch, getState }) => {
			const { results: currentResults } = getState();
			if (!currentResults || !results) {
				return;
			}

			const currentEdges = currentResults.edges;
			const newEdges = results.edges;

			const newResults = {
				...currentResults,
				edges: currentEdges.concat(newEdges),
				pageInfo: results?.pageInfo,
			};

			dispatch(
				setSearchStoreState({
					results: newResults,
					isLoadingMore: false,
				}),
			);
		},
	setFetchFromCursor:
		(cursor: string): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					paginationTokens: [{ cursor }],
				}),
			);
		},
	setIsLoadingMore:
		(isLoadingMore: boolean): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isLoadingMore,
				}),
			);
		},
	setJiraIssueFieldsLoading:
		(isLoading: boolean): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isJiraHydrationLoading: isLoading,
				}),
			);
		},
	setJiraIssueDetails:
		(issueData: HydratedJiraIssue[]): Action<SearchStoreState> =>
		({ dispatch, getState }) => {
			const { results } = getState();
			let hydratedResults: SearchPageEdge[] = [];
			if (results?.edges && results.edges.length > 0 && issueData.length > 0) {
				hydratedResults = [...results.edges].map((edge) => {
					if (isSearchResultJiraIssue(edge.node)) {
						const id = edge.node.id;
						const hydratedIssue = issueData.find((issue) => issue.id === id);

						if (edge.node.status && hydratedIssue) {
							edge.node.status.name = hydratedIssue.statusField.status.name;
						}
					}
					return edge;
				});
				dispatch(
					setSearchStoreState({
						results: {
							...results,
							edges: hydratedResults,
						},
					}),
				);
			}
		},
	setCompassTeamsLoading:
		(isLoading: boolean): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isCompassTeamsLoading: isLoading,
				}),
			);
		},
	setCompassTeams:
		(teamData: TeamDetails[]): Action<SearchStoreState> =>
		({ dispatch, getState }) => {
			const { results } = getState();
			let resultsWithTeamNames: SearchPageEdge[] = [];
			if (results?.edges && results.edges.length > 0 && teamData.length > 0) {
				resultsWithTeamNames = [...results.edges].map((edge) => {
					if (isSearchCompassPartial(edge.node)) {
						const teamId = getTeamId(edge.node.ownerId);
						const team = teamData.find((team) => team.id === teamId);
						edge.node.ownerName = team?.displayName;
					}
					return edge;
				});
				dispatch(
					setSearchStoreState({
						results: {
							...results,
							edges: resultsWithTeamNames,
						},
					}),
				);
			}
		},

	resetResults:
		(): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isLoading: false,
					shownCount: 0,
					results: undefined,
					resultError: undefined,
					requestId: undefined,
				}),
			);
		},
	setAutoCorrection:
		(autoCorrection?: string): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ autoCorrection }));
		},
	setProductResultCount:
		(totalCounts?: ProductCount[]): Action<SearchStoreState> =>
		({ dispatch, getState }) => {
			if (totalCounts === undefined) {
				dispatch(
					setSearchStoreState({
						productResultCountMap: null,
					}),
				);
				return;
			}
			const { productResultCountMap: oldProductResultCountMap } = getState();
			const productResultCountMap = totalCounts.reduce<ProductResultCount>(
				(acc, { product, count }) => {
					if (product) {
						acc[product] = count;
					}
					return acc;
				},
				{},
			);

			dispatch(
				setSearchStoreState({
					productResultCountMap: { ...oldProductResultCountMap, ...productResultCountMap },
				}),
			);
		},
	// For backwards compatibility
	clearHybridFilters:
		(): Action<SearchStoreState> =>
		({ dispatch }) => {
			dispatch(actions.clearAllFilters());
		},

	/**
	 * Considered unsafe since it's using queryParams to change the state.
	 * Ideally we should set it directly in the state, but that requires introducing a new action.
	 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
	 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
	 *
	 * We hope to improve Atlassian Home / global noun handling after GA
	 *
	 * @param value: a noun to be set
	 */
	unsafe_setAtlasNouns:
		(value?: NounKeys): Action<SearchStoreState, SearchStoreContainerProps> =>
		({ dispatch }, { queryParams, setQueryParams }) => {
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);

			if (!value) {
				const { product, type, ...queryParamsWithResetNounFilter } = queryParams;
				setQueryParams({ ...queryParamsWithResetNounFilter });
			} else {
				const { text, originalQuery, lastModified } = queryParams;
				// We explicityly only keep text and lastModified
				setQueryParams({
					product: ProductKeys.Atlas,
					type: value,
					text: text as string,
					originalQuery: originalQuery as string,
					lastModified: lastModified as LastModifiedValue,
				});
			}
		},
} as const;

const clearFilters = (
	allFilterStates: Record<string, AllFilterStates | undefined>,
	filters: AllFilterValues[],
	config: CloudConfig,
) => {
	const clearedFilters = filters.map((filter) => {
		const filterState = allFilterStates[filter.id];

		if (!filter.isCompatible(filterState)) {
			return [];
		}

		return [filter.id, filter.withState(filterState, config).clear().getState()];
	});

	return Object.fromEntries(clearedFilters) as SearchStoreState['filters'];
};

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<SearchStoreState, TActions>({
	initialState,
	actions,
	name: 'dynamic-cards-search',
});

export const useFilterActions: HookActionsFunction<
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>
> = createActionsHook(Store);

export const SearchStoreContainer: OverrideContainerComponent<SearchStoreContainerProps> =
	createContainer<SearchStoreState, TActions, SearchStoreContainerProps>(Store, {
		onInit:
			() =>
			({ dispatch }) => {
				dispatch(privateStoreActions.initialiseStore());
			},
		onUpdate:
			() =>
			({ dispatch }) => {
				dispatch(privateStoreActions.updateStore());
			},
		onCleanup:
			() =>
			({ setState }) => {
				setState(initialState);
			},
	});

// Note - This doesnt trigger a re-render
export const useSearch: HookFunction<
	SearchStoreState,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store);

export const useSearchActions: HookActionsFunction<
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>
> = createActionsHook(Store);

export const useFilters: HookStateFunction<
	Record<string, AllFilterStates | undefined>,
	void
> = createStateHook(Store, {
	selector: (state) => state.filters,
});

// Hooks
export const useInputQuery: HookFunction<
	string,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.inputQuery,
});

export const useSearchQuery: HookFunction<
	string,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.searchQuery,
});

export const useOriginalQuery: HookFunction<
	string | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.originalQuery,
});

export const useAutoCorrection: HookFunction<
	string | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.autoCorrection,
});

export const useRetryCount: HookFunction<
	number,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.retryCount,
});

export const useProductResultCountMap: HookFunction<
	ProductResultCount | null,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.productResultCountMap,
});

export const useABTest: HookFunction<
	AggAbTest | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.results?.abTest,
});

export const useQueryInfo: HookFunction<
	QueryInfo | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.results?.queryInfo,
});

export const useQueryState: HookFunction<
	QueryState,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => (state.searchQuery ? QueryState.POSTQUERY : QueryState.PREQUERY),
});

export const useQueryUpdatedTime: HookFunction<
	number | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.queryUpdatedTime,
});

export const useResults: HookFunction<
	SearchPageResults | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.results,
});

export const useResultsError: HookFunction<
	ResultsFetchError | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.resultError,
});

export const useResultsRequestId: HookFunction<
	string | undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.requestId,
});

export const useIsLoading: HookFunction<
	boolean,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.isLoading,
});

export const useResultsSort: HookFunction<
	ResultsSort,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.resultsSort,
});

// filter hooks
export const useSelectedProducts: HookFunction<
	ProductKeys[],
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.selectedProducts,
});

export const useSelectedProduct: HookFunction<
	ProductKeys,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.selectedProducts[0],
});

export const useIsJiraHydrationLoading: HookStateFunction<boolean | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.isJiraHydrationLoading,
	});

export const useIsCompassTeamsLoading: HookStateFunction<boolean | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.isCompassTeamsLoading,
	});

export const useIsSearchReady: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.availableProducts && state.availableProducts.length > 0,
});

export const usePageEndCursor: HookStateFunction<string | null | undefined, void> = createStateHook(
	Store,
	{
		selector: (state) => state.results?.pageInfo?.endCursor,
	},
);

export const useFetchFromCursor: HookStateFunction<string, void> = createStateHook(Store, {
	selector: (state) => state.paginationTokens[0]?.cursor,
});

export const useIsLoadingMore: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.isLoadingMore,
});

export const useProducts: HookStateFunction<
	{
		availableProducts: ProductKeys[];
		filteredProducts: ProductKeys[];
		selectedProduct: ProductKeys | undefined;
	},
	void
> = createStateHook(Store, {
	selector: createSelector(
		[
			(state) => state.selectedProducts.at(0),
			(state) =>
				state.selectedProducts[0] ? [state.selectedProducts[0]] : state.availableProducts,
			(state) => state.availableProducts,
		],
		(selectedProduct, filteredProducts, availableProducts) => ({
			selectedProduct,
			filteredProducts,
			availableProducts,
		}),
	),
});

export const getUniversalTypeEntities = (
	productsToSearch: ProductKeys[],
	typeFilter: AllFilterStates | undefined,
	outboundAuthConfigs: OutboundAuthConfigsType,
	availableProducts: ProductKeys[],
): {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
	selectedEntities: TypeFilterValueKey[];
} => {
	// This early return is to appease Typescript. The typeFilter is always defined and is always single select.
	if (!typeFilter || !(typeFilter.type === 'single-select')) {
		return {
			selectedEntities: [],
			entitiesForQuery: [],
			entitiesForResultsCountQuery: [],
		};
	}
	const defaultOptionValue = typeFilter.options?.find((option) => option.isDefault)?.value;

	const selectedTypes = (typeFilter.value as TypeFilterValueKey[])
		.filter((value) => value !== defaultOptionValue)
		.filter(Boolean);

	const { entitiesForQuery, entitiesForResultsCountQuery } = getUniversalEntitiesForQuery(
		productsToSearch,
		selectedTypes,
		outboundAuthConfigs,
		availableProducts,
	);

	return {
		selectedEntities: selectedTypes,
		entitiesForQuery: entitiesForQuery,
		entitiesForResultsCountQuery,
	};
};

type UseTypeEntitiesResult = {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
	selectedEntities: string[];
};

export const useTypeEntities: HookStateFunction<
	UseTypeEntitiesResult,
	{
		outboundAuthConfigs: OutboundAuthConfigsType;
	}
> = createStateHook(Store, {
	selector: createSelector(
		[
			(state) => {
				// Filter out excluded products from "All" filter
				const allFilterIncludedProducts = state.availableProducts.filter(
					(product) => !state.allFilterExcludedProducts.includes(product),
				);

				return state.selectedProducts[0] ? [state.selectedProducts[0]] : allFilterIncludedProducts;
			},
			(state) => state.filters[universalTypeFilterId],
			(_, props: { outboundAuthConfigs: OutboundAuthConfigsType }) => props.outboundAuthConfigs,
			(state) => state.availableProducts,
		],
		(productsToSearch, typeFilter, outboundAuthConfigs, availableProducts): UseTypeEntitiesResult =>
			getUniversalTypeEntities(
				productsToSearch,
				typeFilter,
				outboundAuthConfigs,
				availableProducts,
			),
	),
});

export const useSelectFilterStore: HookFunction<
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'lastUpdated';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'contributor';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'space';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'assignee';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'project';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'content';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'owner';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'label';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'universalTypeFilter';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			selectedProducts: ProductKeys[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			hasSelection: boolean;
			id: 'verified';
			products: ProductKeys[];
			queryParams: {
				key: string;
			};
			selectedProducts: ProductKeys[];
			type: 'boolean';
			universal: boolean;
			value: boolean;
	  }
	| {
			allFilterExcludedProducts: ProductKeys[];
			alwaysVisible: boolean;
			availableProducts: ProductKeys[];
			hasSelection: boolean;
			id: 'onlyTitleMatch';
			products: ProductKeys[];
			queryParams: {
				key: string;
			};
			selectedProducts: ProductKeys[];
			type: 'boolean';
			universal: boolean;
			value: boolean;
	  }
	| undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	{
		filterId: string;
	}
> = createHook(Store, {
	selector: (state, props: { filterId: string }) => {
		const filter = state.filters[props.filterId];

		if (filter === undefined) {
			return undefined;
		}

		if (!isSelectFilter(filter.type)) {
			throw new Error('Not a select filter');
		}

		return {
			...filter,
			selectedProducts: state.selectedProducts,
			availableProducts: state.availableProducts,
			allFilterExcludedProducts: state.allFilterExcludedProducts,
		};
	},
});

export const useBooleanFilterStore: HookFunction<
	| Readonly<{
			alwaysVisible: boolean;
			hasSelection: boolean;
			id: 'verified';
			products: ProductKeys[];
			queryParams: {
				key: string;
			};
			type: 'boolean';
			universal: boolean;
			value: boolean;
	  }>
	| Readonly<{
			alwaysVisible: boolean;
			hasSelection: boolean;
			id: 'onlyTitleMatch';
			products: ProductKeys[];
			queryParams: {
				key: string;
			};
			type: 'boolean';
			universal: boolean;
			value: boolean;
	  }>
	| undefined,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	{
		filterId: string;
	}
> = createHook(Store, {
	selector: (state, props: { filterId: string }) => {
		const filter = state.filters[props.filterId];

		if (filter === undefined) {
			return undefined;
		}

		if (filter?.type !== 'boolean') {
			throw new Error('Not a boolean filter');
		}

		return filter;
	},
});

export const getUniversalFilters = (filters: Filters): AllFilterStates[] => {
	const filterIdOrdering = getFilterIdOrdering();
	return filterIdOrdering.flatMap((f) => {
		const filter = filters[f];
		return filter && filter.universal ? [filter] : [];
	});
};

export const getSelectedProductFilters = (
	filters: Filters,
	selectedProducts: ProductKeys[],
): AllFilterStates[] => {
	const filterIdOrdering = getFilterIdOrdering();
	return filterIdOrdering.flatMap((f) => {
		const filter = filters[f];
		return filter && selectedProducts.some((product) => filter?.products.includes(product))
			? [filter]
			: [];
	});
};

/**
 * All available filters in the initial ordering
 */
const getAllAvailableFilters = (filters: Filters, selectedProducts: ProductKeys[]) => {
	return selectedProducts.length === 0
		? getUniversalFilters(filters)
		: getSelectedProductFilters(filters, selectedProducts);
};

/**
 * All available filters are always returned for inline, but the ordering depends on selection
 * The caller UI is responsible for deciding how many can be rendered by available width
 */
const getFilterIdsForInline = (
	filters: Filters,
	selectedProducts: ProductKeys[],
	filterSelectionOrder: string[],
): string[] => {
	const allAvailableFilterIds = getAllAvailableFilters(filters, selectedProducts).map(
		(a) => a.id as string,
	);

	// using the selection order, filter from the available (removes any invalid filters to show)
	let filtersForInline = filterSelectionOrder.filter((f) => allAvailableFilterIds.includes(f));

	// then simply add the rest back (with a set for deduplication)
	return [...new Set([...filtersForInline, ...allAvailableFilterIds])];
};

export const useFiltersToDisplay: HookFunction<
	{
		filterSelectionOrder: string[];
		filtersInDropdown: (
			| 'label'
			| 'content'
			| 'project'
			| 'space'
			| 'verified'
			| 'universalTypeFilter'
			| 'assignee'
			| 'contributor'
			| 'lastUpdated'
			| 'onlyTitleMatch'
			| 'owner'
		)[];
		filtersInLine: string[];
	},
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ filters, selectedProducts, filterSelectionOrder }) => {
		// All filters always show in the dropdown, in order
		const filtersInDropdown = getAllAvailableFilters(filters, selectedProducts).map((f) => f.id);

		const filtersInLine = getFilterIdsForInline(filters, selectedProducts, filterSelectionOrder);

		return {
			filtersInDropdown,
			filtersInLine,
			filterSelectionOrder,
		};
	},
});

export const useSelectedFilterCount: HookFunction<
	{
		selectedFilterCount: number;
	},
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ selectedProducts, filters, resultsSort }) => {
		const selectedFilters = Object.values(filters)
			.filter((filter) => filter?.hasSelection)
			.filter((filter) => {
				const isAtlasSelected = selectedProducts.includes(ProductKeys.Atlas);
				const isTypeFilter = filter?.id === universalTypeFilterId;
				return isAtlasSelected ? !isTypeFilter : true;
			});

		const { field, order } = resultsSort;
		const isSortedByDefault =
			field === initialState.resultsSort.field && order === initialState.resultsSort.order;

		return {
			selectedFilterCount: selectedFilters.length + (isSortedByDefault ? 0 : 1),
		};
	},
});

// TODO: >>>> QS-4163 write tests
export const useAnyFilterSelected: HookFunction<
	boolean,
	BoundActions<
		SearchStoreState,
		{
			readonly appendMoreResults: (
				results: SearchStoreState['results'],
			) => Action<SearchStoreState>;
			readonly clearAllAvailableFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchStoreState, SearchStoreContainerProps>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchStoreState>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly incrementVersion: () => Action<SearchStoreState>;
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly resetResults: () => Action<SearchStoreState>;
			readonly resetSort: () => Action<SearchStoreState>;
			readonly retrySearch: () => Action<SearchStoreState>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchStoreState>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						shouldSetQueryParams?: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchStoreState, SearchStoreContainerProps>;
					// TODO QS-4438 move this out to be private/unsafe like setSearchStoreState
					readonly updateStore: () => Action<SearchStoreState, SearchStoreContainerProps>;
				},
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchStoreState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchStoreState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchStoreState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchStoreState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchStoreState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchStoreState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchStoreState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchStoreState>;
			// Results actions
			readonly setResults: (
				results?: SearchStoreState['results'],
				error?: SearchStoreState['resultError'],
				requestId?: SearchStoreState['requestId'],
			) => Action<SearchStoreState>;
			readonly setResultsSort: ({ field, order }: ResultsSort) => Action<SearchStoreState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchStoreState>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly setSelectFilterOption: (props: {
				customValue?: Record<string, string>;
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			}) => Action<SearchStoreState, SearchStoreContainerProps>;
			readonly triggerSearch: () => Action<SearchStoreState>;
			/**
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
			// TODO: >>>> QS-4163 After removing the old search store, merge selectProductFilter and unselectProductFilter
			// together as they do the same thing
			readonly unselectProductFilter: (
				productKey: ProductKeys,
			) => Action<SearchStoreState, SearchStoreContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ selectedProducts, filters }) => {
		const hasSelectedProduct = selectedProducts[0];
		if (hasSelectedProduct) {
			return true;
		}

		return Object.values(filters).some((filter) => filter?.hasSelection);
	},
});
